import React, { FC, useEffect, useRef } from "react";

import UseRecaptchaService from "../../services/recaptcha.service";

interface GrecaptchaEnterprise {
  render: (
    container: HTMLElement,
    params: {
      sitekey: string;
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    },
  ) => number;
  reset: (widgetId?: number) => void;
}

declare global {
  interface Window {
    grecaptcha?: { enterprise?: GrecaptchaEnterprise };
  }
}

interface ReCaptchaCheckboxProps {
  onSolved: (token: string) => void;
  onExpired?: () => void;
}

/**
 * Renders the visible reCAPTCHA Enterprise checkbox ("I'm not a robot") puzzle.
 * The Enterprise script is already loaded by ReCaptchaProvider (score key), so
 * we reuse the shared `grecaptcha.enterprise` runtime and explicitly render the
 * checkbox key here. Shown only after the backend returns a `challenge` outcome.
 */
const ReCaptchaCheckbox: FC<ReCaptchaCheckboxProps> = ({
  onSolved,
  onExpired,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);

  useEffect(() => {
    const siteKey = UseRecaptchaService.getCheckboxSiteKey();

    if (!siteKey) {
      console.error("ReCaptcha checkbox site key is not configured");
      return;
    }

    let cancelled = false;

    const tryRender = () => {
      if (cancelled) {
        return;
      }

      const enterprise = window.grecaptcha?.enterprise;

      if (!enterprise?.render || !containerRef.current) {
        window.setTimeout(tryRender, 200);
        return;
      }

      // Guard against double render (StrictMode / re-invocation).
      if (
        widgetIdRef.current !== null ||
        containerRef.current.childNodes.length > 0
      ) {
        return;
      }

      widgetIdRef.current = enterprise.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => onSolved(token),
        "expired-callback": () => onExpired?.(),
      });
    };

    tryRender();

    return () => {
      cancelled = true;
    };
  }, [onSolved, onExpired]);

  return <div ref={containerRef} className="recaptcha-checkbox" />;
};

export default ReCaptchaCheckbox;
